/**
 * The `Device info:` block of the feedback report (#1249): one labelled line
 * per fact instead of the old `JSON.stringify(client)` with quotes, commas and
 * braces stripped, which printed `connection` as nothing (a host object has no
 * own properties), listed a dozen `false` flags, and gave the screen and
 * window size from when the app loaded rather than when the report was sent.
 */

import type {ConnectionLike} from "./resultClient";

interface UaBrand {
  brand: string;
  version: string;
}

interface Size {
  width: number;
  height: number;
}

export interface DeviceInfoInput {
  userAgent?: string;
  userAgentData?: {
    brands?: ReadonlyArray<UaBrand>;
    mobile?: boolean;
    platform?: string;
  };
  screen: Size;
  window: Size;
  maxTouchPoints?: number;
  hardwareConcurrency?: number;
  deviceMemory?: number;
  connection?: ConnectionLike;
  languages?: ReadonlyArray<string>;
  cookieEnabled?: boolean;
  doNotTrack?: string | null;
  browser?: object;
  device?: object;
  bot?: object;
}

const UNAVAILABLE = "unavailable";

const orUnavailable = (value: unknown, suffix = ""): string =>
  value === undefined || value === null || value === ""
    ? UNAVAILABLE
    : `${String(value)}${suffix}`;

const yesNo = (value: boolean | undefined): string =>
  value === undefined ? UNAVAILABLE : value ? "yes" : "no";

/** Names of the flags that are `true`; the `false` ones carry no information. */
const trueFlags = (flags: unknown): string[] | undefined =>
  flags && typeof flags === "object"
    ? Object.entries(flags)
        .filter(([, value]) => value === true)
        .map(([name]) => name)
    : undefined;

const formatFlags = (flags: unknown): string => {
  const names = trueFlags(flags);
  if (!names) return UNAVAILABLE;
  return names.length > 0 ? names.join(", ") : "none";
};

const formatBrands = (data: DeviceInfoInput["userAgentData"]): string => {
  const brands = data?.brands;
  if (!brands || brands.length === 0) return UNAVAILABLE;
  const list = brands.map((b) => `${b.brand} ${b.version}`).join(", ");
  return `${list} (mobile: ${yesNo(data.mobile)}, platform: ${orUnavailable(data.platform)})`;
};

const formatConnection = (connection: ConnectionLike | undefined): string =>
  connection?.effectiveType
    ? `${connection.effectiveType}, downlink ${orUnavailable(connection.downlink, " Mbps")}, rtt ${orUnavailable(connection.rtt, " ms")}, save-data ${yesNo(connection.saveData)}`
    : UNAVAILABLE;

/**
 * `isBot` is the verdict; the other keys are the heuristics behind it, named
 * so a reader can judge a false positive.
 */
const formatBot = (bot: unknown): string => {
  const names = trueFlags(bot);
  if (!names) return UNAVAILABLE;
  if (!names.includes("isBot")) return "no";
  const reasons = names.filter((name) => name !== "isBot");
  return reasons.length > 0 ? `yes (${reasons.join(", ")})` : "yes";
};

export const formatDeviceInfo = (input: DeviceInfoInput): string =>
  [
    `User agent: ${orUnavailable(input.userAgent)}`,
    `UA brands: ${formatBrands(input.userAgentData)}`,
    `Screen: ${input.screen.width}x${input.screen.height}, window ${input.window.width}x${input.window.height}`,
    `Touch points: ${orUnavailable(input.maxTouchPoints)}`,
    `CPU cores: ${orUnavailable(input.hardwareConcurrency)}`,
    `Device memory: ${orUnavailable(input.deviceMemory, " GB")}`,
    `Connection: ${formatConnection(input.connection)}`,
    `Languages: ${input.languages?.length ? input.languages.join(", ") : UNAVAILABLE}`,
    `Cookies: ${input.cookieEnabled === undefined ? UNAVAILABLE : input.cookieEnabled ? "enabled" : "disabled"}`,
    `Do not track: ${orUnavailable(input.doNotTrack ?? "unspecified")}`,
    `Browser: ${formatFlags(input.browser)}`,
    `Device: ${formatFlags(input.device)}`,
    `Bot: ${formatBot(input.bot)}`,
  ].join("\n");
